/// Tag model config — the single read point for device capabilities on Web.
///
/// WHERE THE CONFIG LIVES (changed 2026-08, SYS-tag-model): the models are rows in the
/// backend `tag_spec` system setting, editable by a Super Admin at
/// システム設定 > BLEトラッカーモデル.
/// `tag-model-store` hydrates this registry once per session from `GET /tag-models`;
/// every component keeps importing the same SYNCHRONOUS helpers as before.
///
/// `BUILTIN_TAG_MODEL_SPECS` is NOT the source of truth any more — it is the offline
/// FALLBACK used before hydration and when the request fails, so a backend hiccup shows
/// today's capabilities instead of hiding battery/buzzer everywhere. It must stay
/// identical to the backend fallback; the guard test
/// `NodeJS/src/modules/tag/__tests__/tag-spec.mirror.test.ts` fails CI on drift.
import { shallowRef } from 'vue'

/** `setting_key` carrying the model config — mirrors the backend constant. */
export const TAG_SPEC_SETTING_KEY = 'tag_spec'

/** Capabilities + naming of one device model. */
export interface TagSpec {
  getLocation: boolean
  /** Has a buzzer. */
  buzzer: boolean
  /** Battery percentage can be read. */
  getBatteryPercent: boolean
  /** Chart slice color, `#RRGGBB`. */
  chartColor?: string
}

/** Offline fallback — mirrors the backend `BUILTIN_TAG_MODEL_SPECS`. */
export const BUILTIN_TAG_MODEL_SPECS: Record<string, TagSpec> = {
  PB706: {
    getLocation: true,
    buzzer: true,
    getBatteryPercent: true,
    chartColor: '#3b82f6',
  },
  // LPK-03 = keychain (Jimi SKU PB704). The old name LPS-03 was retired in 2026-08; there
  // is no alias concept any more — one model, one name.
  'LPK-03': {
    getLocation: true,
    buzzer: true,
    getBatteryPercent: true,
    chartColor: '#10b981',
  },
  'LPS-02': {
    getLocation: true,
    buzzer: false,
    getBatteryPercent: false,
    chartColor: '#f59e0b',
  },
}

// ── Registry ─────────────────────────────────────────────────────────────────────
//
// REACTIVE on purpose. Hydration happens asynchronously AFTER the app mounts, so a
// component that read the registry during setup would otherwise keep the fallback
// forever. `shallowRef` makes every helper below a reactive dependency: a `computed`
// calling `canGetBattery()` / `tagModelDisplay()` re-evaluates when hydrate lands.
// Shallow is enough — entries are replaced wholesale, never mutated in place.

const specsRef = shallowRef<Record<string, TagSpec>>(BUILTIN_TAG_MODEL_SPECS)
const orderedModelsRef = shallowRef<string[]>(Object.keys(BUILTIN_TAG_MODEL_SPECS))

/**
 * Replace the registry with what the API returned (called by `tag-model-store.hydrate`).
 * `models` fixes the display order; without it the object key order is used.
 */
export function setTagSpecs(next: Record<string, TagSpec>, models?: string[]): void {
  specsRef.value = next
  orderedModelsRef.value = models ?? Object.keys(next)
}

/**
 * Restore the offline fallback. Test hook — the app never calls it: a failed hydrate
 * simply leaves the registry untouched, which already IS the fallback.
 */
export function resetTagSpecs(): void {
  setTagSpecs(BUILTIN_TAG_MODEL_SPECS)
}

// ── Lookups ──────────────────────────────────────────────────────────────────────

/** Canonical models in display order (dropdowns, chart order). */
export function tagModels(): string[] {
  return orderedModelsRef.value
}

/**
 * Normalise a label for comparison: trim + upper-case. Nothing else.
 *
 * No alias table any more — the `tag_spec` setting is the only source of truth, and a
 * label it does not contain is simply an unknown model ("other").
 */
export function canonicalTagModel(model: string): string {
  return model.trim().toUpperCase()
}

export function getTagSpec(model: string): TagSpec | undefined {
  return specsRef.value[canonicalTagModel(model)]
}

/**
 * Models with a buzzer.
 *
 * ⚠️ NO CONSUMER YET — no "play sound" button exists anywhere in Web. Kept because
 * `buzzer` is spec-mandated config (input/spec.md §II); the admin screen labels it
 * "ring action not implemented yet".
 */
export function canBuzz(model: string): boolean {
  return getTagSpec(model)?.buzzer ?? false
}

/** Models that report battery %; others hide the battery UI. */
export function canGetBattery(model: string): boolean {
  return getTagSpec(model)?.getBatteryPercent ?? false
}

export function canGetLocation(model: string): boolean {
  return getTagSpec(model)?.getLocation ?? false
}

/**
 * Chart order + color per canonical model. Colors come from the config (`chart_color`)
 * so adding a model on the admin screen gives it a slice color with no code change;
 * a model saved without one falls back to a neutral color rather than dropping the slice.
 */
export function tagModelDisplay(): { key: string; color: string }[] {
  return orderedModelsRef.value.map((key) => ({
    key,
    color: specsRef.value[key]?.chartColor ?? TAG_MODEL_FALLBACK_COLOR,
  }))
}

/** Fallback slice color for a model saved without one — never drop it from the chart. */
export const TAG_MODEL_FALLBACK_COLOR = '#9ca3af'
